import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, EmptyState, Pagination } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import ListPreLoaderItem from "app/pages/sales-intelligence/common-components/ListPreLoaderItem/ListPreLoaderItem";
import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";

import { SIGNAL_RECOMMENDER_FILES_PAGE_SIZE } from "../../constants";
import type { SignalRecommenderFile } from "../../types/model";
import { clampPage } from "../../utils/clampPage";
import signalRecommenderI18nKeys from "../../utils/i18n";

import { DealSignalFileItem } from "./DealSignalFileItem";
import { StyledEmptyStateWrapper, StyledFilesCard, StyledPaginationWrapper } from "./styles";

type SignalRecommenderFilesListProps = {
    readonly files: readonly SignalRecommenderFile[];
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly downloadingFileId: number | null;
    readonly refreshingFileId: number | null;
    /** Rendered inside the empty state, below the copy. Must be referentially stable: `EmptyState`
     *  mounts it as a component, so a fresh closure remounts the button and its tooltip. */
    readonly emptyStateCta?: FC;
    readonly onDownload: (file: SignalRecommenderFile) => void;
    readonly onRefresh: (file: SignalRecommenderFile) => void;
    readonly onRetry: VoidFunction;
};

export const SignalRecommenderFilesList = ({
    files,
    isLoading,
    isError,
    downloadingFileId,
    refreshingFileId,
    emptyStateCta,
    onDownload,
    onRefresh,
    onRetry,
}: SignalRecommenderFilesListProps) => {
    const { translate } = useTranslate();
    const [requestedPage, setRequestedPage] = useState(1);

    const page = clampPage(requestedPage, files.length, SIGNAL_RECOMMENDER_FILES_PAGE_SIZE);

    // `EmptyState` mounts this as a component, so it has to stay referentially stable across renders.
    const renderRetryCta = useCallback(
        () => (
            <Button
                onClick={onRetry}
                data-automation="signal-recommender-list-retry"
            >
                {translate("si.common.refresh")}
            </Button>
        ),
        [onRetry, translate],
    );

    const renderPaginationCaption = ({ itemsCount }: { itemsCount: number }) => {
        const caption = signalRecommenderI18nKeys.list.pagination({
            page,
            total: formatNumberWithComa(Math.ceil(itemsCount / SIGNAL_RECOMMENDER_FILES_PAGE_SIZE)),
        });

        return translate(caption.key, caption.replacements);
    };

    const paginatedFiles = useMemo(() => {
        const startIndex = (page - 1) * SIGNAL_RECOMMENDER_FILES_PAGE_SIZE;

        return files.slice(startIndex, startIndex + SIGNAL_RECOMMENDER_FILES_PAGE_SIZE);
    }, [files, page]);

    if (isLoading) {
        return (
            <StyledFilesCard>
                <Stack
                    gap="8px"
                    padding="8px"
                >
                    <ListPreLoaderItem rowNumber={3} />
                </Stack>
            </StyledFilesCard>
        );
    }

    if (isError) {
        return (
            <StyledFilesCard>
                <StyledEmptyStateWrapper
                    padding="44px 0"
                    margin="0 auto"
                    width="360px"
                    data-automation="signal-recommender-list-error"
                >
                    <EmptyState
                        title={translate("common.error")}
                        subtitle={translate(signalRecommenderI18nKeys.list.error)}
                        iconName="no-data-tree"
                        CtaButtons={renderRetryCta}
                    />
                </StyledEmptyStateWrapper>
            </StyledFilesCard>
        );
    }

    if (files.length === 0) {
        return (
            <StyledFilesCard>
                <StyledEmptyStateWrapper
                    padding="44px 0"
                    margin="0 auto"
                    width="360px"
                    data-automation="signal-recommender-list-empty"
                >
                    <EmptyState
                        title={translate(signalRecommenderI18nKeys.list.empty.title)}
                        subtitle={translate(signalRecommenderI18nKeys.list.empty.subtitle)}
                        CtaButtons={emptyStateCta}
                    />
                </StyledEmptyStateWrapper>
            </StyledFilesCard>
        );
    }

    return (
        <StyledFilesCard data-automation="signal-recommender-files-list">
            <Stack
                gap="8px"
                padding="8px"
            >
                {paginatedFiles.map((file) => (
                    <DealSignalFileItem
                        key={file.id}
                        file={file}
                        isDownloading={file.id === downloadingFileId}
                        isRefreshing={file.id === refreshingFileId}
                        onDownload={onDownload}
                        onRefresh={onRefresh}
                    />
                ))}
            </Stack>

            {files.length > SIGNAL_RECOMMENDER_FILES_PAGE_SIZE ? (
                <StyledPaginationWrapper>
                    <Pagination
                        page={page}
                        itemsCount={files.length}
                        itemsPerPage={SIGNAL_RECOMMENDER_FILES_PAGE_SIZE}
                        captionPosition="center"
                        hasItemsPerPageSelect={false}
                        handlePageChange={setRequestedPage}
                        captionElement={renderPaginationCaption}
                    />
                </StyledPaginationWrapper>
            ) : null}
        </StyledFilesCard>
    );
};
